import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fabric } from 'fabric';
import {
  FiMove, FiEdit2, FiMinus, FiSquare, FiType, FiTrash2, FiSave, FiX,
  FiArrowUpRight, FiTag, FiRotateCcw,
} from 'react-icons/fi';

/**
 * Fabric.js markup editor for a single photo.
 *
 * Deliberately generic over where the photo lives: the caller passes a loadable
 * `imageUrl` (an object URL, since the photo endpoints need a bearer token an
 * <img> would not send) and an `onSave({ json, dataUrl })` callback. That lets
 * the same editor serve letter photos and job photos without knowing about
 * either.
 *
 * Markup is stored two ways on save: the fabric JSON so it stays re-editable,
 * and a flattened PNG at the photo's native resolution that is what actually
 * prints. The original upload is never modified.
 */

const TOOLS = {
  SELECT: 'select',
  DRAW: 'draw',
  LINE: 'line',
  ARROW: 'arrow',
  RECT: 'rect',
  TEXT: 'text',
  BMP: 'bmp',
};

const COLORS = ['#dc2626', '#f59e0b', '#16a34a', '#2563eb', '#7c3aed', '#000000', '#ffffff'];

// Custom props that must survive toJSON/loadFromJSON so a BMP block, its leader
// and its target stay linked across an edit session.
const CUSTOM_PROPS = ['bmpId', 'bmpRole', 'bmpCode'];

/**
 * Build one BMP block: a boxed code, a leader line, and a draggable target dot.
 *
 * Three linked objects rather than one group, so the label and the point it
 * indicates can be dragged independently -- which is what you need when the
 * feature sits in a corner of the frame. They stay associated by a shared
 * bmpId. Exported so the geometry can be exercised without mounting the editor.
 */
export const createBmpBlock = ({ code, x, y, color, idSeed }) => {
  const bmpId = `bmp_${idSeed}`;
  // Offset the label up and left so the leader is visible, clamped into frame.
  const labelX = Math.max(8, x - 110);
  const labelY = Math.max(8, y - 70);

  const text = new fabric.Text(code, {
    fontSize: 18, fontFamily: 'Arial, Helvetica, sans-serif', fontWeight: 'bold',
    fill: '#111111', originX: 'center', originY: 'center',
  });
  const box = new fabric.Rect({
    width: Math.max(58, text.width + 22), height: text.height + 14,
    fill: '#ffffff', stroke: color, strokeWidth: 2, rx: 3, ry: 3,
    originX: 'center', originY: 'center',
  });
  const label = new fabric.Group([box, text], {
    left: labelX, top: labelY, originX: 'center', originY: 'center',
    bmpId, bmpRole: 'label', bmpCode: code, hasControls: false,
  });
  const leader = new fabric.Line([labelX, labelY, x, y], {
    stroke: color, strokeWidth: 2, selectable: false, evented: false,
    objectCaching: false, bmpId, bmpRole: 'leader',
  });
  const target = new fabric.Circle({
    left: x, top: y, radius: 5, fill: color, stroke: '#ffffff', strokeWidth: 2,
    originX: 'center', originY: 'center', hasControls: false,
    bmpId, bmpRole: 'target',
  });

  return { bmpId, label, leader, target };
};

/** Re-aim one BMP's leader line at its label and target. */
export const relinkBmp = (canvas, bmpId) => {
  const parts = canvas.getObjects().filter(o => o.bmpId === bmpId);
  const label = parts.find(o => o.bmpRole === 'label');
  const leader = parts.find(o => o.bmpRole === 'leader');
  const target = parts.find(o => o.bmpRole === 'target');
  if (!label || !leader || !target) return;
  const l = label.getCenterPoint();
  const t = target.getCenterPoint();
  leader.set({ x1: l.x, y1: l.y, x2: t.x, y2: t.y });
  leader.setCoords();
};

const relinkAll = (canvas) => {
  const ids = new Set(canvas.getObjects().map(o => o.bmpId).filter(Boolean));
  ids.forEach(id => relinkBmp(canvas, id));
};

function PhotoAnnotator({ imageUrl, annotationJson, bmpSymbols, onSave, onClose, title }) {
  const canvasRef = useRef(null);
  const fabricRef = useRef(null);
  const wrapperRef = useRef(null);
  // Native pixel size of the photo, so the export can be written at full
  // resolution rather than at whatever size it happened to display.
  const naturalSize = useRef({ w: 0, h: 0 });
  const drawState = useRef({});

  const [activeTool, setActiveTool] = useState(TOOLS.SELECT);
  const [activeColor, setActiveColor] = useState('#dc2626');
  const [lineWidth, setLineWidth] = useState(4);
  const [bmpCode, setBmpCode] = useState('');
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!bmpCode && bmpSymbols?.length) setBmpCode(bmpSymbols[0].code);
  }, [bmpSymbols, bmpCode]);

  // ---- Canvas bring-up -------------------------------------------------
  useEffect(() => {
    if (!canvasRef.current || !imageUrl) return undefined;
    const canvas = new fabric.Canvas(canvasRef.current, {
      // Light, not near-black: if the photo ever fails to load, an empty grey
      // canvas reads as "no image" rather than as a dark photograph.
      backgroundColor: '#f3f4f6',
      preserveObjectStacking: true,
    });
    fabricRef.current = canvas;

    fabric.Image.fromURL(imageUrl, (img) => {
      if (!img || !img.width) return;
      naturalSize.current = { w: img.width, h: img.height };

      // Fit the photo to the available width, capped so tall photos stay usable.
      const maxW = Math.min(wrapperRef.current?.clientWidth || 900, 1000);
      const maxH = Math.min(window.innerHeight - 260, 720);
      const scale = Math.min(maxW / img.width, maxH / img.height, 1);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);

      canvas.setDimensions({ width: w, height: h });

      // Applied only once the saved markup is in place: loadFromJSON clears the
      // background even when the JSON carries none, which would leave the photo
      // gone and only the canvas fill showing.
      const applyPhotoBackground = () => {
        img.set({ selectable: false, evented: false });
        canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas), {
          scaleX: w / img.width,
          scaleY: h / img.height,
        });
      };

      if (annotationJson) {
        try {
          const data = typeof annotationJson === 'string'
            ? JSON.parse(annotationJson) : annotationJson;
          // The live photo is the background; never let a stored one win.
          delete data.backgroundImage;
          delete data.background;

          // Markup is stored in the canvas pixels of whatever window it was
          // drawn in. Reopening on a narrower screen gives a smaller canvas, so
          // rescale everything or the markup drifts off the features it marks.
          const savedW = Number(data._canvasWidth) || 0;
          const ratio = savedW > 0 ? w / savedW : 1;

          canvas.loadFromJSON(data, () => {
            if (ratio !== 1 && Number.isFinite(ratio) && ratio > 0) {
              canvas.getObjects().forEach((o) => {
                o.set({
                  left: (o.left || 0) * ratio,
                  top: (o.top || 0) * ratio,
                  scaleX: (o.scaleX || 1) * ratio,
                  scaleY: (o.scaleY || 1) * ratio,
                });
                o.setCoords();
              });
            }
            // Leaders are recomputed from their label and target, so they
            // survive the rescale without being scaled themselves.
            relinkAll(canvas);
            applyPhotoBackground();
            canvas.renderAll();
            setReady(true);
          });
          return;
        } catch (err) {
          console.warn('Could not restore markup, starting clean:', err);
        }
      }
      applyPhotoBackground();
      setReady(true);
    }, { crossOrigin: 'anonymous' });

    const markDirty = () => setDirty(true);
    canvas.on('object:added', markDirty);
    canvas.on('object:modified', markDirty);
    canvas.on('object:removed', markDirty);
    canvas.on('object:moving', (e) => {
      if (e.target?.bmpId) relinkBmp(canvas, e.target.bmpId);
    });

    return () => {
      canvas.dispose();
      fabricRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl]);

  // ---- Brush settings --------------------------------------------------
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    canvas.isDrawingMode = activeTool === TOOLS.DRAW;
    canvas.selection = activeTool === TOOLS.SELECT;
    if (canvas.freeDrawingBrush) {
      canvas.freeDrawingBrush.color = activeColor;
      canvas.freeDrawingBrush.width = lineWidth;
    }
    canvas.defaultCursor = activeTool === TOOLS.SELECT ? 'default' : 'crosshair';
    canvas.forEachObject((o) => {
      // Only the select tool should grab objects; otherwise clicks are for drawing.
      o.selectable = activeTool === TOOLS.SELECT && o.bmpRole !== 'leader';
      o.evented = activeTool === TOOLS.SELECT && o.bmpRole !== 'leader';
    });
    canvas.renderAll();
  }, [activeTool, activeColor, lineWidth, ready]);

  // ---- BMP block -------------------------------------------------------
  const placeBmp = useCallback((canvas, code, x, y) => {
    const { bmpId, label, leader, target } = createBmpBlock({
      code, x, y, color: activeColor,
      idSeed: `${Date.now()}_${Math.round(x)}_${Math.round(y)}`,
    });
    canvas.add(leader, target, label);
    relinkBmp(canvas, bmpId);
    canvas.renderAll();
  }, [activeColor]);

  // ---- Click / drag tools ---------------------------------------------
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !ready) return undefined;

    const onDown = (opt) => {
      if (activeTool === TOOLS.SELECT || activeTool === TOOLS.DRAW) return;
      const p = canvas.getPointer(opt.e);

      if (activeTool === TOOLS.BMP) {
        if (!bmpCode) {
          alert('Add a BMP code in Settings first.');
          return;
        }
        placeBmp(canvas, bmpCode, p.x, p.y);
        setActiveTool(TOOLS.SELECT);
        return;
      }

      if (activeTool === TOOLS.TEXT) {
        const t = new fabric.IText('Text', {
          left: p.x, top: p.y, fill: activeColor,
          fontSize: 22, fontFamily: 'Arial, Helvetica, sans-serif', fontWeight: 'bold',
          // A white halo keeps text legible over a busy photo.
          stroke: '#ffffff', strokeWidth: 0.6, paintFirst: 'stroke',
        });
        canvas.add(t);
        canvas.setActiveObject(t);
        t.enterEditing();
        t.selectAll();
        setActiveTool(TOOLS.SELECT);
        return;
      }

      drawState.current = { x: p.x, y: p.y, shape: null };

      if (activeTool === TOOLS.LINE || activeTool === TOOLS.ARROW) {
        const line = new fabric.Line([p.x, p.y, p.x, p.y], {
          stroke: activeColor, strokeWidth: lineWidth,
          objectCaching: false, selectable: false, evented: false,
        });
        canvas.add(line);
        drawState.current.shape = line;
      } else if (activeTool === TOOLS.RECT) {
        const rect = new fabric.Rect({
          left: p.x, top: p.y, width: 1, height: 1,
          fill: 'transparent', stroke: activeColor, strokeWidth: lineWidth,
          selectable: false, evented: false,
        });
        canvas.add(rect);
        drawState.current.shape = rect;
      }
    };

    const onMove = (opt) => {
      const st = drawState.current;
      if (!st.shape) return;
      const p = canvas.getPointer(opt.e);
      if (st.shape.type === 'line') {
        st.shape.set({ x2: p.x, y2: p.y });
      } else {
        st.shape.set({
          left: Math.min(st.x, p.x),
          top: Math.min(st.y, p.y),
          width: Math.abs(p.x - st.x),
          height: Math.abs(p.y - st.y),
        });
      }
      canvas.renderAll();
    };

    const onUp = () => {
      const st = drawState.current;
      if (!st.shape) return;
      const shape = st.shape;

      // An arrow is the line plus a head, grouped so they move together.
      if (activeTool === TOOLS.ARROW && shape.type === 'line') {
        const { x1, y1, x2, y2 } = shape;
        const angle = Math.atan2(y2 - y1, x2 - x1);
        const head = new fabric.Triangle({
          left: x2, top: y2,
          width: lineWidth * 4.5, height: lineWidth * 4.5,
          fill: activeColor, originX: 'center', originY: 'center',
          angle: (angle * 180) / Math.PI + 90,
        });
        canvas.remove(shape);
        const arrow = new fabric.Group([
          new fabric.Line([x1, y1, x2, y2], {
            stroke: activeColor, strokeWidth: lineWidth, objectCaching: false,
          }),
          head,
        ]);
        canvas.add(arrow);
      } else {
        shape.set({ selectable: true, evented: true });
        shape.setCoords();
      }

      drawState.current = {};
      canvas.renderAll();
      setActiveTool(TOOLS.SELECT);
    };

    canvas.on('mouse:down', onDown);
    canvas.on('mouse:move', onMove);
    canvas.on('mouse:up', onUp);
    return () => {
      canvas.off('mouse:down', onDown);
      canvas.off('mouse:move', onMove);
      canvas.off('mouse:up', onUp);
    };
  }, [activeTool, activeColor, lineWidth, bmpCode, ready, placeBmp]);

  // ---- Actions ---------------------------------------------------------
  const deleteSelected = () => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const active = canvas.getActiveObjects();
    active.forEach((obj) => {
      // Removing any part of a BMP removes the whole block.
      if (obj.bmpId) {
        canvas.getObjects().filter(o => o.bmpId === obj.bmpId).forEach(o => canvas.remove(o));
      } else {
        canvas.remove(obj);
      }
    });
    canvas.discardActiveObject();
    canvas.renderAll();
  };

  const clearAll = () => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    if (!window.confirm('Remove all markup from this photo?')) return;
    canvas.getObjects().slice().forEach(o => canvas.remove(o));
    canvas.renderAll();
    setDirty(true);
  };

  const handleSave = async () => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    setSaving(true);
    try {
      canvas.discardActiveObject();
      canvas.renderAll();
      const saved = canvas.toJSON(CUSTOM_PROPS);
      // Remember the canvas this markup was drawn against, so reopening at a
      // different window size can rescale it back onto the same features.
      saved._canvasWidth = canvas.getWidth();
      saved._canvasHeight = canvas.getHeight();
      const json = JSON.stringify(saved);
      // Export at the photo's native resolution so print quality is unchanged.
      const multiplier = naturalSize.current.w
        ? naturalSize.current.w / canvas.getWidth()
        : 1;
      // JPEG, not PNG: re-encoding a photo as PNG measured ~4.2x the original
      // file size on real site photos, where JPEG q90 lands at ~0.8x for no
      // visible loss. The canvas is fully covered by the photo, so there is no
      // transparency to preserve.
      const dataUrl = canvas.toDataURL({ format: 'jpeg', quality: 0.9, multiplier });
      const hasMarkup = canvas.getObjects().length > 0;
      await onSave({ json, dataUrl, hasMarkup });
      setDirty(false);
    } catch (err) {
      alert('Could not save markup: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const requestClose = () => {
    if (dirty && !window.confirm('Close without saving your markup?')) return;
    onClose();
  };

  // ---- UI --------------------------------------------------------------
  const toolBtn = (tool, icon, label) => (
    <button
      type="button"
      onClick={() => setActiveTool(tool)}
      title={label}
      style={{
        padding: '8px 10px',
        background: activeTool === tool ? '#0f2942' : 'white',
        color: activeTool === tool ? 'white' : '#374151',
        border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12.5px',
      }}
    >
      {icon}<span style={{ display: 'none' }}>{label}</span>
    </button>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.72)', zIndex: 3000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
    }}>
      <div style={{
        background: '#f9fafb', borderRadius: '12px', maxWidth: '1100px', width: '100%',
        maxHeight: '96vh', overflow: 'auto', boxShadow: '0 18px 50px rgba(0,0,0,.4)',
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 18px', borderBottom: '1px solid #e5e7eb', background: 'white',
          borderRadius: '12px 12px 0 0',
        }}>
          <strong style={{ fontSize: '15px', color: '#111827' }}>
            {title || 'Mark Up Photo'}
          </strong>
          <button
            onClick={requestClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', lineHeight: 0 }}
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Toolbar */}
        <div style={{
          display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center',
          padding: '10px 18px', borderBottom: '1px solid #e5e7eb', background: 'white',
        }}>
          {toolBtn(TOOLS.SELECT, <FiMove size={15} />, 'Select')}
          {toolBtn(TOOLS.DRAW, <FiEdit2 size={15} />, 'Freehand')}
          {toolBtn(TOOLS.LINE, <FiMinus size={15} />, 'Line')}
          {toolBtn(TOOLS.ARROW, <FiArrowUpRight size={15} />, 'Arrow')}
          {toolBtn(TOOLS.RECT, <FiSquare size={15} />, 'Rectangle')}
          {toolBtn(TOOLS.TEXT, <FiType size={15} />, 'Text')}

          <div style={{ width: '1px', alignSelf: 'stretch', background: '#e5e7eb', margin: '0 2px' }} />

          {/* BMP block */}
          <select
            value={bmpCode}
            onChange={(e) => setBmpCode(e.target.value)}
            style={{ padding: '7px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
          >
            {!bmpSymbols?.length && <option value="">No BMP codes</option>}
            {(bmpSymbols || []).map(s => (
              <option key={s.id} value={s.code}>
                {s.code}{s.description ? ` — ${s.description}` : ''}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setActiveTool(TOOLS.BMP)}
            disabled={!bmpSymbols?.length}
            title="Click the photo where this BMP applies"
            style={{
              padding: '8px 12px',
              background: activeTool === TOOLS.BMP ? '#0f2942' : 'white',
              color: activeTool === TOOLS.BMP ? 'white' : '#374151',
              border: '1px solid #d1d5db', borderRadius: '6px',
              cursor: bmpSymbols?.length ? 'pointer' : 'not-allowed',
              display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12.5px', fontWeight: 600,
            }}
          >
            <FiTag size={14} /> Place BMP
          </button>

          <div style={{ width: '1px', alignSelf: 'stretch', background: '#e5e7eb', margin: '0 2px' }} />

          {COLORS.map(c => (
            <button
              key={c}
              type="button"
              onClick={() => setActiveColor(c)}
              title={c}
              style={{
                width: '24px', height: '24px', borderRadius: '5px', background: c,
                border: activeColor === c ? '3px solid #0f2942' : '1px solid #d1d5db',
                cursor: 'pointer', padding: 0,
              }}
            />
          ))}
          <input
            type="range" min="1" max="12" value={lineWidth}
            onChange={(e) => setLineWidth(parseInt(e.target.value, 10))}
            title={`Line width: ${lineWidth}`}
            style={{ width: '84px' }}
          />

          <div style={{ flex: 1 }} />

          <button type="button" onClick={deleteSelected} title="Delete selected"
            style={{ padding: '8px 10px', background: 'white', color: '#dc2626', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', lineHeight: 0 }}>
            <FiTrash2 size={15} />
          </button>
          <button type="button" onClick={clearAll} title="Clear all markup"
            style={{ padding: '8px 10px', background: 'white', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', lineHeight: 0 }}>
            <FiRotateCcw size={15} />
          </button>
        </div>

        {/* Canvas */}
        <div ref={wrapperRef} style={{ padding: '18px', display: 'flex', justifyContent: 'center' }}>
          <canvas ref={canvasRef} style={{ border: '1px solid #d1d5db', borderRadius: '6px', maxWidth: '100%' }} />
        </div>

        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px',
          padding: '12px 18px', borderTop: '1px solid #e5e7eb', background: 'white',
          borderRadius: '0 0 12px 12px', flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>
            {activeTool === TOOLS.BMP
              ? 'Click the photo where this BMP applies — drag the block or its dot to adjust.'
              : 'The original photo is kept; markup can be edited or removed any time.'}
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={requestClose}
              style={{ padding: '10px 16px', background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving}
              style={{ padding: '10px 18px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', cursor: saving ? 'wait' : 'pointer', fontSize: '13px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <FiSave size={14} /> {saving ? 'Saving...' : 'Save Markup'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PhotoAnnotator;
